import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Calendar,
  Pencil,
  Trash2,
  X,
  Save,
  Search,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import CreateButton from '../components/CreateButton';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateInput = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0,00 €';
  return num.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};

const getRowStatus = (row) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dates = [row.fecha_1, row.fecha_2, row.fecha_3].filter(Boolean).map((d) => {
    const parsed = new Date(d);
    parsed.setHours(0, 0, 0, 0);
    return parsed;
  });

  if (dates.length === 0) return 'normal';

  const hasOverdue = dates.some((d) => d < today);
  if (hasOverdue) return 'overdue';

  const thirtyDays = new Date(today);
  thirtyDays.setDate(thirtyDays.getDate() + 30);
  const hasUpcoming = dates.some((d) => d >= today && d <= thirtyDays);
  if (hasUpcoming) return 'upcoming';

  return 'normal';
};

const STATUS_STYLES = {
  overdue: { bg: 'bg-red-50', border: 'border-l-4 border-l-red-400', badge: 'bg-red-100 text-red-700', label: 'Vencido' },
  upcoming: { bg: 'bg-amber-50', border: 'border-l-4 border-l-amber-400', badge: 'bg-amber-100 text-amber-700', label: 'Próximo' },
  normal: { bg: '', border: 'border-l-4 border-l-transparent', badge: 'bg-green-100 text-green-700', label: 'Al día' },
};

const ITEMS_PER_PAGE = 20;

const DueDateManagement = () => {
  const { token, API_URL, user } = useAuth();
  const [dueDates, setDueDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  // Form
  const [form, setForm] = useState({ id_cliente: '', factura: '', total: '', fecha_1: '', fecha_2: '', fecha_3: '' });

  // Client search for modal
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  const fetchDueDates = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: currentPage, limit: ITEMS_PER_PAGE });
      if (desde) params.set('desde', desde);
      if (hasta) params.set('hasta', hasta);
      if (searchTerm) params.set('search', searchTerm);

      const res = await fetch(`${API_URL}/due-dates?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al cargar vencimientos');
      const data = await res.json();
      setDueDates(data.dueDates);
      setTotalItems(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, currentPage, desde, hasta, searchTerm]);

  useEffect(() => {
    fetchDueDates();
  }, [fetchDueDates]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, desde, hasta]);

  // Client search for modal
  useEffect(() => {
    if (clientSearch.length < 2) {
      setClientResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/clients/search?q=${encodeURIComponent(clientSearch)}&limit=10`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setClientResults(data.clients || []);
          setShowClientDropdown(true);
        }
      } catch {
        // silent
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [clientSearch, API_URL, token]);

  const openCreate = () => {
    setEditingItem(null);
    setForm({ id_cliente: '', factura: '', total: '', fecha_1: '', fecha_2: '', fecha_3: '' });
    setClientSearch('');
    setSelectedClient(null);
    setClientResults([]);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      id_cliente: item.id_cliente,
      factura: item.factura,
      total: item.total,
      fecha_1: formatDateInput(item.fecha_1),
      fecha_2: formatDateInput(item.fecha_2),
      fecha_3: formatDateInput(item.fecha_3),
    });
    setSelectedClient({ id: item.id_cliente, razon_social: item.cliente_nombre });
    setClientSearch(item.cliente_nombre || '');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.id_cliente || !form.factura || !form.total || !form.fecha_1) {
      setError('Cliente, factura, total y fecha 1 son obligatorios');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const url = editingItem ? `${API_URL}/due-dates/${editingItem.id}` : `${API_URL}/due-dates`;
      const method = editingItem ? 'PUT' : 'POST';

      const body = {
        id_cliente: form.id_cliente,
        factura: form.factura,
        total: parseFloat(form.total),
        fecha_1: form.fecha_1,
        fecha_2: form.fecha_2 || null,
        fecha_3: form.fecha_3 || null,
      };

      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al guardar');

      setShowModal(false);
      fetchDueDates();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este vencimiento?')) return;
    try {
      const res = await fetch(`${API_URL}/due-dates/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al eliminar');
      fetchDueDates();
    } catch (err) {
      setError(err.message);
    }
  };

  const selectClient = (client) => {
    setSelectedClient(client);
    setForm({ ...form, id_cliente: client.id });
    setClientSearch(client.razon_social || client.alias || `ID ${client.id}`);
    setShowClientDropdown(false);
    setClientResults([]);
  };

  // Compute visible total
  const visibleTotal = dueDates.reduce((sum, row) => sum + parseFloat(row.total || 0), 0);

  const canDelete = user && (user.permisos === 1 || user.permisos === 6);

  if (loading && dueDates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#0099FF' }}></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header bar */}
      <div className="bg-white shadow-sm border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
              <Calendar className="w-6 h-6" style={{ color: '#0099FF' }} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Vencimientos</h1>
              <p className="text-sm text-gray-500">Control de fechas de vencimiento de facturas</p>
            </div>
          </div>
          <CreateButton onClick={openCreate}>Nuevo Vencimiento</CreateButton>
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mt-4 flex items-center justify-between">
            <span className="text-sm flex items-center gap-2">
              <AlertCircle size={16} />
              {error}
            </span>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">
              <X size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Filters bar */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Buscar</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por cliente o número de factura..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          {(desde || hasta || searchTerm) && (
            <button
              onClick={() => {
                setDesde('');
                setHasta('');
                setSearchTerm('');
              }}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Table (scrollable) */}
      <div className="flex-1 overflow-auto p-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Factura</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha 1</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha 2</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha 3</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dueDates.map((row) => {
                const status = getRowStatus(row);
                const style = STATUS_STYLES[status];
                return (
                  <tr key={row.id} className={`${style.bg} ${style.border} hover:bg-gray-50/50 transition-colors`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.factura}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{row.cliente_nombre || `ID ${row.id_cliente}`}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">{formatCurrency(row.total)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center">{formatDate(row.fecha_1)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center">{formatDate(row.fecha_2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center">{formatDate(row.fecha_3)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${style.badge}`}>{style.label}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(row)}
                          className="p-1.5 rounded text-gray-400 hover:text-blue-600 transition-colors"
                          title="Editar"
                        >
                          <Pencil size={15} />
                        </button>
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(row.id)}
                            className="p-1.5 rounded text-gray-400 hover:text-red-500 transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {dueDates.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <Calendar size={36} className="mx-auto mb-3 text-gray-300" />
                    <p className="text-sm text-gray-500">No se encontraron vencimientos</p>
                    <p className="text-xs text-gray-400 mt-1">Intente con otros criterios de búsqueda</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Totals row */}
        {dueDates.length > 0 && (
          <div className="mt-3 bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-3 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600">
              Total visible ({dueDates.length} registro{dueDates.length !== 1 ? 's' : ''})
            </span>
            <span className="text-base font-bold" style={{ color: '#0077CC' }}>
              {formatCurrency(visibleTotal)}
            </span>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1} a{' '}
              {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} de {totalItems} registros
            </p>
            <div className="flex space-x-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
              >
                <ChevronLeft size={14} /> Anterior
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                      currentPage === pageNum
                        ? 'text-white'
                        : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                    style={currentPage === pageNum ? { backgroundColor: '#0099FF' } : {}}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
              >
                Siguiente <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingItem ? 'Editar Vencimiento' : 'Nuevo Vencimiento'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Client search */}
                <div className="md:col-span-2 relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cliente *</label>
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={clientSearch}
                      onChange={(e) => {
                        setClientSearch(e.target.value);
                        setSelectedClient(null);
                        setForm({ ...form, id_cliente: '' });
                      }}
                      placeholder="Buscar cliente por nombre, CIF o ID..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  {selectedClient && (
                    <p className="text-xs text-green-600 mt-1">
                      Seleccionado: {selectedClient.razon_social} (ID: {selectedClient.id})
                    </p>
                  )}
                  {showClientDropdown && clientResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto">
                      {clientResults.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => selectClient(c)}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                        >
                          <span className="font-medium">{c.razon_social || c.alias}</span>
                          <span className="text-gray-400 ml-2">ID: {c.id}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Factura *</label>
                  <input
                    type="text"
                    value={form.factura}
                    onChange={(e) => setForm({ ...form, factura: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.total}
                    onChange={(e) => setForm({ ...form, total: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha 1 *</label>
                  <input
                    type="date"
                    value={form.fecha_1}
                    onChange={(e) => setForm({ ...form, fecha_1: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha 2</label>
                  <input
                    type="date"
                    value={form.fecha_2}
                    onChange={(e) => setForm({ ...form, fecha_2: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha 3</label>
                  <input
                    type="date"
                    value={form.fecha_3}
                    onChange={(e) => setForm({ ...form, fecha_3: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: '#0099FF' }}
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save size={14} />
                )}
                {editingItem ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DueDateManagement;
